import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  BuildingOfficeIcon, 
  DocumentTextIcon,
  CubeIcon,
  CheckCircleIcon,
  UserIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { torrefacoesService } from '../../../services/torrefacoes';
import { cityService } from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import LogoUpload from '../../../components/ui/LogoUpload';
import CoverImageUpload from '../../../components/ui/CoverImageUpload';
import PlaceBusinessHoursMatrix from '../../../components/PlaceBusinessHoursMatrix';
import apiClient from '../../../services/api';



interface UpdateBasicInfoRequest {
  name: string;
  description: string;
  address: string;
  cityId: string;
  instagram: string;
  logoUrl: string;
  coverImageUrl: string;
}

interface UpdateBusinessInfoRequest {
  companyName: string;
  cnpj: string;
  phone: string;
  email: string;
  website: string;
  businessHours: string;
}

const TorrefacaoEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [activeSection, setActiveSection] = useState<'basic' | 'business' | 'hours' | 'products' | 'users'>('basic');
  const [basicInfo, setBasicInfo] = useState<UpdateBasicInfoRequest>({
    name: '',
    description: '',
    address: '',
    cityId: '',
    instagram: '',
    logoUrl: '',
    coverImageUrl: '',
  });

  const [businessInfo, setBusinessInfo] = useState<UpdateBusinessInfoRequest>({
    companyName: '',
    cnpj: '',
    phone: '',
    email: '',
    website: '',
    businessHours: '',
  });

  const [logoUrl, setLogoUrl] = useState('');
  const [coverImageUrl, setCoverImageUrl] = useState('');

  // Buscar torrefação
  const { data: torrefacao, isLoading: isLoadingTorrefacao } = useQuery({
    queryKey: ['torrefacao', id],
    queryFn: () => torrefacoesService.getTorrefacaoById(id!),
    enabled: !!id,
  });

  // Buscar cidades
  const { data: cities = [] } = useQuery({
    queryKey: ['cities'],
    queryFn: cityService.getAllCities,
  });


  // Atualizar dados básicos
  const updateBasicInfoMutation = useMutation({
    mutationFn: (data: UpdateBasicInfoRequest) => torrefacoesService.updateTorrefacao(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['torrefacao', id] });
      alert('Dados básicos atualizados com sucesso!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Erro ao atualizar dados básicos');
    },
  });

  // Atualizar dados empresariais
  const updateBusinessInfoMutation = useMutation({
    mutationFn: (data: UpdateBusinessInfoRequest) => torrefacoesService.updateTorrefacao(id!, {
      ...basicInfo,
      ...data
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['torrefacao', id] });
      alert('Dados empresariais atualizados com sucesso!');
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Erro ao atualizar dados empresariais');
    },
  });

  // Preencher formulário quando os dados carregarem
  useEffect(() => {
    if (torrefacao) {
      setBasicInfo({
        name: torrefacao.name || '',
        description: torrefacao.description || '',
        address: torrefacao.address || '',
        cityId: torrefacao.cityId || '',
        instagram: torrefacao.instagram || '',
        logoUrl: torrefacao.logoUrl || '',
        coverImageUrl: torrefacao.coverImageUrl || '',
      });
      setBusinessInfo({
        companyName: '',
        cnpj: torrefacao.cnpj || '',
        phone: torrefacao.phone || '',
        email: torrefacao.email || '',
        website: torrefacao.website || '',
        businessHours: '',
      });
      setLogoUrl(torrefacao.logoUrl || '');
      setCoverImageUrl(torrefacao.coverImageUrl || '');
    }
  }, [torrefacao]);

  const handleLogoUpload = (imageUrl: string) => {
    setLogoUrl(imageUrl);
    setBasicInfo(prev => ({
      ...prev,
      logoUrl: imageUrl
    }));
  };

  const handleCoverImageUpload = (imageUrl: string) => {
    setCoverImageUrl(imageUrl);
    setBasicInfo(prev => ({
      ...prev,
      coverImageUrl: imageUrl
    }));
  };

  const handleBasicInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateBasicInfoMutation.mutateAsync(basicInfo);
    } catch (error) {
      console.error('Erro ao atualizar dados básicos:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBusinessInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await updateBusinessInfoMutation.mutateAsync(businessInfo);
    } catch (error) {
      console.error('Erro ao atualizar dados empresariais:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingTorrefacao) {
    return <LoadingSpinner />;
  }

  if (!torrefacao) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Torrefação não encontrada</h1>
          <Link to="/torrefacoes" className="text-blue-600 hover:text-blue-800">
            Voltar para Torrefações
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/torrefacoes')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            {torrefacao.logoUrl ? (
              <img 
                src={torrefacao.logoUrl} 
                alt="Logo da torrefação" 
                className="h-8 w-8 rounded-lg object-cover"
              />
            ) : (
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Editar Torrefação</h1>
              <p className="text-gray-600">{torrefacao.name}</p>
            </div>
          </div>
        </div>

        {/* Navegação das seções */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveSection('basic')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'basic'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-4 w-4" />
              Dados Básicos
            </div>
          </button>
          <button
            onClick={() => setActiveSection('business')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'business'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4" />
              Dados Empresariais
            </div>
          </button>
          <button
            onClick={() => setActiveSection('hours')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'hours'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <ClockIcon className="h-4 w-4" />
              Horários
            </div>
          </button>
          <button
            onClick={() => setActiveSection('products')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'products'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <CubeIcon className="h-4 w-2" />
              Produtos
            </div>
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'users'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              Usuários
            </div>
          </button>
        </div>
      </div>

      {/* Seção Ativa */}
      <div className="bg-white rounded-lg shadow">
        {activeSection === 'basic' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Dados Básicos</h2>
            
            <form onSubmit={handleBasicInfoSubmit} className="space-y-6">
              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Torrefação
                  </label>
                  <input
                    type="text"
                    value={basicInfo.name}
                    onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="text"
                    value={basicInfo.instagram}
                    onChange={(e) => setBasicInfo({ ...basicInfo, instagram: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="@usuario"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={basicInfo.description}
                  onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Descreva a torrefação..."
                />
              </div>

              {/* Localização */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cidade
                  </label>
                  <select
                    value={basicInfo.cityId}
                    onChange={(e) => setBasicInfo({ ...basicInfo, cityId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  >
                    <option value="">Selecione uma cidade</option>
                    {cities.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name}, {city.stateName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Endereço
                  </label>
                  <input
                    type="text"
                    value={basicInfo.address}
                    onChange={(e) => setBasicInfo({ ...basicInfo, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Imagens */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo
                  </label>
                  <LogoUpload
                    currentImageUrl={logoUrl}
                    onImageUploaded={handleLogoUpload}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagem de Capa
                  </label>
                  <CoverImageUpload
                    currentImageUrl={coverImageUrl}
                    onImageUploaded={handleCoverImageUpload}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting || updateBasicInfoMutation.isPending}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {isSubmitting || updateBasicInfoMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      Salvar Dados Básicos
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeSection === 'business' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Dados Empresariais</h2>
            
            <form onSubmit={handleBusinessInfoSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Empresa
                  </label>
                  <input
                    type="text"
                    value={businessInfo.companyName}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    value={businessInfo.cnpj}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, cnpj: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={businessInfo.phone}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    E-mail
                  </label>
                  <input
                    type="email"
                    value={businessInfo.email}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="contato@torrefacao.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={businessInfo.website}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="https://www.torrefacao.com"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting || updateBusinessInfoMutation.isPending}
                  className="btn btn-primary flex items-center gap-2"
                >
                  {isSubmitting || updateBusinessInfoMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      Salvar Dados Empresariais
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeSection === 'hours' && (
          <div className="p-6">
            <PlaceBusinessHoursMatrix placeId={id!} />
          </div>
        )}

        {activeSection === 'products' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Produtos</h2>
            <p className="text-gray-600">Gerenciamento de produtos será implementado em breve.</p>
          </div>
        )}

        {activeSection === 'users' && (
          <TorrefacaoUsersSection placeId={id!} />
        )}
      </div>
    </div>
  );
};

// Componente para gerenciar usuários vinculados à torrefação
const TorrefacaoUsersSection: React.FC<{ placeId: string }> = ({ placeId }) => {
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm, setLinkForm] = useState({
    businessUserId: '',
    role: 'Owner'
  });

  // Buscar usuários vinculados à torrefação
  const { data: placeUsers, isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['place-users', placeId],
    queryFn: async () => {
      const response = await apiClient.get(`/admin/places/${placeId}/users`);
      return response.data;
    },
    enabled: !!placeId
  });

  // Buscar todos os business users disponíveis para vincular
  const { data: availableUsers, isLoading: isLoadingAvailableUsers } = useQuery({
    queryKey: ['available-business-users', placeId],
    queryFn: async () => {
      const response = await apiClient.get('/admin/business-users', {
        params: {
          page: 1,
          pageSize: 50
        }
      });
      return response.data.businessUsers;
    }
  });

  // Mutação para vincular usuário
  const linkUserMutation = useMutation({
    mutationFn: async (data: { businessUserId: string; role: string }) => {
      const response = await apiClient.post(`/admin/places/${placeId}/users`, data);
      return response.data;
    },
    onSuccess: () => {
      refetchUsers();
      setShowLinkForm(false);
      setLinkForm({ businessUserId: '', role: 'Owner' });
      alert('Usuário vinculado com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao vincular usuário:', error);
      alert(error.response?.data?.error || 'Erro ao vincular usuário');
    }
  });

  // Mutação para desvincular usuário
  const unlinkUserMutation = useMutation({
    mutationFn: async (businessUserId: string) => {
      const response = await apiClient.delete(`/admin/places/${placeId}/users/${businessUserId}`);
      return response.data;
    },
    onSuccess: () => {
      refetchUsers();
      alert('Usuário desvinculado com sucesso!');
    },
    onError: (error: any) => {
      console.error('Erro ao desvincular usuário:', error);
      alert(error.response?.data?.error || 'Erro ao desvincular usuário');
    }
  });

  const handleLinkUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkForm.businessUserId) {
      alert('Selecione um usuário');
      return;
    }
    linkUserMutation.mutate(linkForm);
  };

  const handleUnlinkUser = (businessUserId: string) => {
    if (confirm('Tem certeza que deseja desvincular este usuário?')) {
      unlinkUserMutation.mutate(businessUserId);
    }
  };

  // Filtrar usuários que não estão vinculados
  const availableUsersToLink = availableUsers?.filter((user: any) =>
    !placeUsers?.users?.some((placeUser: any) => placeUser.id === user.id)
  ) || [];

  if (isLoadingUsers || isLoadingAvailableUsers) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Usuários Vinculados</h2>
        <button 
          onClick={() => setShowLinkForm(!showLinkForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <UserIcon className="h-4 w-4" />
          {showLinkForm ? 'Cancelar' : 'Vincular Usuário'}
        </button>
      </div>

      {/* Formulário para vincular usuário */}
      {showLinkForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vincular Novo Usuário</h3>
          <form onSubmit={handleLinkUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuário *
                </label>
                <select
                  required
                  value={linkForm.businessUserId}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, businessUserId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Selecione um usuário</option>
                  {availableUsersToLink.map((user: any) => (
                    <option key={user.id} value={user.id}>
                      {user.fullName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função na Torrefação *
                </label>
                <select
                  required
                  value={linkForm.role}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="Owner">Proprietário</option>
                  <option value="Admin">Administrador</option>
                  <option value="Manager">Gerente</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowLinkForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={linkUserMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {linkUserMutation.isPending ? 'Vinculando...' : 'Vincular Usuário'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de usuários vinculados */}
      {placeUsers?.users && placeUsers.users.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Função
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vinculado em
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {placeUsers.users.map((user: any) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.linkedAt ? new Date(user.linkedAt).toLocaleDateString('pt-BR') : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleUnlinkUser(user.id)}
                      disabled={unlinkUserMutation.isPending}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Desvincular usuário"
                    >
                      Desvincular
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário vinculado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Vincule usuários para gerenciar esta torrefação.
          </p>
        </div>
      )}
    </div>
  );
};

export default TorrefacaoEditPage;